import { createContext, useCallback, useContext, useState } from "react";
import { AlertTriangle, CheckCircle2, X } from "lucide-react";
import { cn } from "@/shared/lib/cn";

export type ToastVariant = "default" | "success" | "warning";

export type ToastOptions = {
  title?: string;
  message: string;
  variant?: ToastVariant;
  durationMs?: number;
};

type ToastItem = ToastOptions & {
  id: string;
};

type ToastContextValue = {
  showToast: (messageOrOptions: string | ToastOptions) => void;
};

const ToastContext = createContext<ToastContextValue>({
  showToast: () => {},
});

const variantStyles: Record<ToastVariant, string> = {
  default: "border-border bg-card",
  success: "border-green/30 bg-card",
  warning: "border-yellow/40 bg-card",
};

const variantIcons: Record<Exclude<ToastVariant, "default">, React.ComponentType<{ size?: number; className?: string }>> = {
  success: CheckCircle2,
  warning: AlertTriangle,
};

const variantIconStyles: Record<Exclude<ToastVariant, "default">, string> = {
  success: "text-green",
  warning: "text-yellow",
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (messageOrOptions: string | ToastOptions) => {
      const options: ToastOptions =
        typeof messageOrOptions === "string"
          ? { message: messageOrOptions, variant: "default", durationMs: 3200 }
          : {
              variant: "default",
              durationMs: 3200,
              ...messageOrOptions,
            };

      const id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;

      const toast: ToastItem = { id, ...options };
      setToasts((current) => [...current, toast]);

      window.setTimeout(() => {
        dismissToast(id);
      }, options.durationMs ?? 3200);
    },
    [dismissToast],
  );

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="pointer-events-none fixed right-4 top-20 z-[120] flex w-full max-w-sm flex-col gap-3 sm:right-6">
        {toasts.map((toast) => {
          const variant = toast.variant ?? "default";
          const Icon = variant !== "default" ? variantIcons[variant] : null;

          return (
            <div
              key={toast.id}
              role="status"
              className={cn(
                "pointer-events-auto rounded-2xl border p-4 shadow-[var(--shadow)] transition-all",
                variantStyles[variant],
              )}
            >
              <div className="flex items-start gap-3">
                {Icon && variant !== "default" && (
                  <div
                    className={cn(
                      "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-card-muted",
                      variantIconStyles[variant],
                    )}
                  >
                    <Icon size={18} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  {toast.title && (
                    <p className="text-sm font-semibold text-foreground">{toast.title}</p>
                  )}
                  <p className={cn("text-sm text-muted-foreground", toast.title && "mt-1")}>
                    {toast.message}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => dismissToast(toast.id)}
                  className="shrink-0 rounded-lg p-1 text-muted transition-colors hover:bg-accent/20 hover:text-foreground"
                  aria-label="Fermer"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
